import { useState } from 'react';
import QRCode from 'qrcode';
import { useUIStore } from '@/state/store';
import bamoaLogo from '@/assets/bamoa-logo.jpg';

type Step = 'password' | 'totp' | 'enroll';

export function Login() {
  const setView = useUIStore((s) => s.setView);
  const [email, setEmail] = useState('alice@bamoa.local');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [step, setStep] = useState<Step>('password');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Enrollment-Daten
  const [enrollSecret, setEnrollSecret] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  function finishLogin(data: any) {
    localStorage.setItem('bamoa_token', data.token);
    localStorage.setItem('bamoa_user_id', data.user.id);
    localStorage.setItem('bamoa_user_name', data.user.displayName);
    localStorage.setItem('bamoa_tenant_id', data.user.tenantId);
    if (data.user.tenantName) localStorage.setItem('bamoa_tenant_name', data.user.tenantName);
    if (data.user.tenantLogo) localStorage.setItem('bamoa_tenant_logo', data.user.tenantLogo);
    else localStorage.removeItem('bamoa_tenant_logo');
    if (data.user.role) localStorage.setItem('bamoa_user_role', data.user.role);
    window.dispatchEvent(new CustomEvent('bamoa:logged-in', { detail: data.user }));
    setView('dashboard');
  }

  async function post(path: string, body: any) {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`${res.status} ${t}`);
    }
    return res.json();
  }

  async function submitPassword() {
    setError(null); setBusy(true);
    try {
      const data = await post('/api/auth/dev/login', { email, password });
      if (data.status === 'ok') return finishLogin(data);
      if (data.status === 'totp-required') { setStep('totp'); return; }
      if (data.status === 'enrollment-required') {
        // Secret holen und Enrollment starten
        const enroll = await post('/api/auth/dev/totp/enroll', { email, password });
        setEnrollSecret(enroll.secret);
        // QR-Code lokal erzeugen — Secret verlässt NIE den Client
        try {
          const dataUrl = await QRCode.toDataURL(enroll.otpauthUrl, { width: 200, margin: 1 });
          setQrDataUrl(dataUrl);
        } catch { setQrDataUrl(null); }
        setStep('enroll');
        return;
      }
      throw new Error('Unerwartete Antwort');
    } catch (e: any) {
      setError(`Anmeldung fehlgeschlagen: ${e.message}`);
    } finally { setBusy(false); }
  }

  async function submitTotp() {
    setError(null); setBusy(true);
    try {
      const data = await post('/api/auth/dev/login', { email, password, totpCode });
      if (data.status === 'ok') return finishLogin(data);
      throw new Error('Code abgelehnt');
    } catch (e: any) {
      setError(`2FA fehlgeschlagen: ${e.message}`);
    } finally { setBusy(false); }
  }

  async function submitEnrollConfirm() {
    setError(null); setBusy(true);
    try {
      const data = await post('/api/auth/dev/totp/confirm', { email, password, code: totpCode });
      if (data.status === 'ok') return finishLogin(data);
      throw new Error('Code abgelehnt');
    } catch (e: any) {
      setError(`Aktivierung fehlgeschlagen: ${e.message}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="login-stage view active">
      <div className="login-card">
        <div className="login-brand">
          <img src={bamoaLogo} alt="Bamoa" />
          <div className="login-app-name">Videochat</div>
        </div>

        {step === 'password' && (
          <>
            <div className="login-headline">Anmelden</div>
            <div className="login-form">
              <input type="email" placeholder="E-Mail" value={email}
                onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                onKeyDown={(e) => e.key === 'Enter' && submitPassword()} />
              <input type="password" placeholder="Passwort" value={password}
                onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                onKeyDown={(e) => e.key === 'Enter' && submitPassword()} />
              {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: -4 }}>{error}</div>}
              <button onClick={submitPassword} disabled={busy || !password}>
                {busy ? 'Anmelden…' : 'Anmelden'}
              </button>
            </div>
          </>
        )}

        {step === 'totp' && (
          <>
            <div className="login-headline">Zwei-Faktor-Bestätigung</div>
            <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 14, lineHeight: 1.5 }}>
              Bitte den 6-stelligen Code aus deiner Authenticator-App eingeben.
            </div>
            <div className="login-form">
              <input type="text" inputMode="numeric" placeholder="000000" value={totpCode}
                maxLength={6} autoFocus
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && submitTotp()}
                style={{ letterSpacing: 4, textAlign: 'center', fontSize: 20 }} />
              {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: -4 }}>{error}</div>}
              <button onClick={submitTotp} disabled={busy || totpCode.length !== 6}>
                {busy ? 'Prüfe…' : 'Bestätigen'}
              </button>
              <button onClick={() => { setStep('password'); setTotpCode(''); setError(null); }}
                style={{ background: 'transparent', border: 'none', opacity: 0.6, fontSize: 12 }}>
                Zurück
              </button>
            </div>
          </>
        )}

        {step === 'enroll' && (
          <>
            <div className="login-headline">2FA einrichten</div>
            <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 12, lineHeight: 1.5 }}>
              Dieser Mandant verlangt Zwei-Faktor-Authentifizierung. Scanne den Code
              mit einer Authenticator-App (Google Authenticator, Aegis, …) oder gib
              das Geheimnis manuell ein.
            </div>
            {qrDataUrl && (
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <img alt="QR-Code" width={200} height={200} src={qrDataUrl}
                  style={{ background: '#fff', borderRadius: 8, padding: 6 }} />
              </div>
            )}
            {enrollSecret && (
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12, textAlign: 'center' }}>
                Manuell: <code style={{ fontSize: 13, letterSpacing: 1 }}>{enrollSecret}</code>
              </div>
            )}
            <div className="login-form">
              <input type="text" inputMode="numeric" placeholder="000000" value={totpCode}
                maxLength={6} autoFocus
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && submitEnrollConfirm()}
                style={{ letterSpacing: 4, textAlign: 'center', fontSize: 20 }} />
              {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: -4 }}>{error}</div>}
              <button onClick={submitEnrollConfirm} disabled={busy || totpCode.length !== 6}>
                {busy ? 'Aktiviere…' : 'Aktivieren & anmelden'}
              </button>
            </div>
          </>
        )}

        <div className="login-foot">
          Bamoa Videochat · PoC-Auth (Dev) · 2FA mandantenkonfigurierbar
        </div>
      </div>
    </div>
  );
}
